import React, { useState } from 'react';
import {
  ArrowLeftIcon,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PlusIcon,
  SearchInput,
  SearchIcon,
  Text,
} from '@razorpay/blade/components';
import { useHistory } from 'react-router-dom';
import { EmployeeFieldDefinition } from '../types';
import { useEmployeeFieldDefinitions } from '../hooks';
import { EmployeeFieldsTable } from './EmployeeFieldsTable';
import { AddEmployeeFieldModal } from './AddEmployeeFieldModal';
import { EmployeeFieldsSearch, EmployeeFieldsTabLayout, FixedFooter } from '../styles';

export const EmployeeFieldsTab = () => {
  const history = useHistory();
  const {
    filteredFields,
    search,
    setSearch,
    currentPage,
    pageSize,
    totalFields,
    isLoading,
    isFetching,
    isError,
    refetch,
    onPageChange,
    onPageSizeChange,
    createFieldMutation,
    updateFieldMutation,
    deleteFieldMutation,
  } = useEmployeeFieldDefinitions();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<EmployeeFieldDefinition | null>(null);
  const [deletingField, setDeletingField] = useState<EmployeeFieldDefinition | null>(null);
  const isSearchEmpty = Boolean(search.trim()) && filteredFields.length === 0;

  return (
    <>
      <EmployeeFieldsTabLayout>
        <Box width="100%" maxWidth="720px" marginX="auto" paddingBottom="spacing.11">
          <EmployeeFieldsTable
            fields={filteredFields}
            emptyMessage={
              isSearchEmpty
                ? 'No fields match your search. Try searching by field name, type, or group.'
                : 'No employee fields found. Create a custom field to collect additional employee information.'
            }
            toolbarActions={
              <Box
                position="relative"
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                width="fit-content"
                height="36px"
                marginLeft="auto"
                justifySelf="end">
                <EmployeeFieldsSearch $isExpanded={isSearchOpen}>
                  <div className="collapsed-search-trigger" aria-hidden={isSearchOpen}>
                    <Button
                      variant="tertiary"
                      size="medium"
                      icon={SearchIcon}
                      accessibilityLabel="Search fields"
                      isDisabled={isSearchOpen}
                      onClick={() => setIsSearchOpen(true)}
                    />
                  </div>
                  <div className="expanded-search-input" aria-hidden={!isSearchOpen}>
                    <SearchInput
                      accessibilityLabel="Search employee fields"
                      placeholder="Search fields"
                      size="medium"
                      isDisabled={!isSearchOpen}
                      autoFocus={isSearchOpen}
                      value={search}
                      onChange={({ value }) => setSearch(value ?? '')}
                      onClearButtonClick={() => setSearch('')}
                      onBlur={() => {
                        if (!search.trim()) setIsSearchOpen(false);
                      }}
                    />
                  </div>
                </EmployeeFieldsSearch>
                <Box width="max-content" minWidth="max-content" flexShrink={0}>
                  <Button
                    variant="secondary"
                    size="medium"
                    icon={PlusIcon}
                    iconPosition="left"
                    onClick={() => setIsModalOpen(true)}>
                    Add New
                  </Button>
                </Box>
              </Box>
            }
            toolbarTitle={`Showing ${filteredFields.length} fields`}
            onEdit={(field) => setEditingField(field)}
            onDelete={(field) => setDeletingField(field)}
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            onRetry={() => refetch()}
            currentPage={currentPage}
            pageSize={pageSize}
            totalFields={totalFields}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Box>

        <FixedFooter>
          <Box
            width="100%"
            maxWidth="720px"
            marginX="auto"
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            <Button
              variant="secondary"
              icon={ArrowLeftIcon}
              onClick={() => history.push('/settings')}>
              Back
            </Button>
          </Box>
        </FixedFooter>
      </EmployeeFieldsTabLayout>

      <AddEmployeeFieldModal
        isOpen={isModalOpen}
        onDismiss={() => setIsModalOpen(false)}
        isSubmitting={createFieldMutation.isLoading}
        onCreate={(input) => {
          createFieldMutation.mutate(input, {
            onSuccess: () => setIsModalOpen(false),
          });
        }}
      />

      <AddEmployeeFieldModal
        mode="edit"
        initialField={editingField}
        isOpen={Boolean(editingField)}
        onDismiss={() => setEditingField(null)}
        isSubmitting={updateFieldMutation.isLoading}
        onCreate={() => undefined}
        onUpdate={(fieldId, input) => {
          const fieldToUpdate = editingField;
          if (!fieldToUpdate) return;
          updateFieldMutation.mutate(
            {
              fieldKey: fieldToUpdate.fieldKey,
              input,
              displayOrder: fieldToUpdate.displayOrder,
            },
            {
              onSuccess: () => setEditingField(null),
            },
          );
        }}
      />

      <Modal isOpen={Boolean(deletingField)} onDismiss={() => setDeletingField(null)} size="small">
        <ModalHeader title="Delete field" />
        <ModalBody>
          <Text color="surface.text.gray.muted">
            This will delete {deletingField?.label ?? 'this field'} from employee fields. This
            action cannot be undone.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Box display="flex" justifyContent="flex-end" gap="spacing.3" width="100%">
            <Button variant="tertiary" onClick={() => setDeletingField(null)}>
              Cancel
            </Button>
            <Button
              color="negative"
              isLoading={deleteFieldMutation.isLoading}
              onClick={() => {
                if (!deletingField) return;
                deleteFieldMutation.mutate(deletingField.fieldKey, {
                  onSuccess: () => setDeletingField(null),
                });
              }}>
              Delete
            </Button>
          </Box>
        </ModalFooter>
      </Modal>
    </>
  );
};
